import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { clearAuthLocalStorage } from '../authentication/authStorage';
import {
  isExpiredSpaRefreshTokenError,
  isSilentTokenAcquisitionTimeoutError,
} from './msalError';
import { setApiToken } from '../api';
import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';

export function useAccessToken() {
  const { instance, accounts, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const authRecoveryStarted = useRef(false);

  const account = accounts[0];

  useEffect(() => {
    let isMounted = true;

    if (!account) {
      authRecoveryStarted.current = false;
      setAccessToken(null);
      return () => {
        isMounted = false;
      };
    }

    if (inProgress !== InteractionStatus.None) {
      return () => {
        isMounted = false;
      };
    }

    const request = {
      scopes: ['User.Read'],
      account,
    };

    instance
      .acquireTokenSilent(request)
      .then((response) => {
        if (isMounted) {
          authRecoveryStarted.current = false;
          setAccessToken(response.accessToken);
        }
      })
      .catch(async (error) => {
        if (!isMounted || authRecoveryStarted.current) return;

        if (
          isExpiredSpaRefreshTokenError(error) ||
          isSilentTokenAcquisitionTimeoutError(error)
        ) {
          authRecoveryStarted.current = true;
          setAccessToken(null);
          clearAuthLocalStorage();
          setApiToken();

          try {
            // logoutRedirect clears MSAL's cache and the Microsoft Entra
            // server session before returning to the configured login URI.
            await instance.logoutRedirect({ account });
            /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- Preserve verified legacy behavior. */
          } catch (logoutError) {
            // App auth is already cleared. Avoid leaving the user on a spinner
            // if MSAL cannot start the server-side logout navigation.
            if (isMounted) window.location.replace('/login');
          }
          return;
        }

        // Other interaction-required errors (for example MFA or consent)
        // should use a top-level redirect so popup blocking cannot strand the UI.
        if (error instanceof InteractionRequiredAuthError) {
          authRecoveryStarted.current = true;

          try {
            await instance.acquireTokenRedirect(request);
            /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- Preserve verified legacy behavior. */
          } catch (redirectError) {
            authRecoveryStarted.current = false;
          }
        }
      });

    return () => {
      isMounted = false;
    };
  }, [account, inProgress, instance]);

  return accessToken;
}
